import { supabase } from "@/integrations/supabase/client";
import { AMA, AMAQuestion, AMAAnswer, AMAStatus, AMAQuestionStatus } from "@/types/ama";

export class AMAService {
  // AMA CRUD operations
  static async getAllAMAs(): Promise<AMA[]> {
    const { data, error } = await supabase
      .from('amas')
      .select('*')
      .order('scheduled_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async getAMAById(id: string): Promise<AMA | null> {
    const { data, error } = await supabase
      .from('amas')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  static async createAMA(ama: Partial<AMA>): Promise<AMA> {
    const { data, error } = await supabase
      .from('amas')
      .insert([ama as any])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateAMA(id: string, updates: Partial<AMA>): Promise<AMA> {
    const { data, error } = await supabase
      .from('amas')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteAMA(id: string): Promise<void> {
    const { error } = await supabase
      .from('amas')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  static async archiveAMA(id: string): Promise<AMA> {
    const { data, error } = await supabase
      .from('amas')
      .update({ status: 'archived' })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Question operations
  static async getQuestionsByAMA(amaId: string): Promise<AMAQuestion[]> {
    const { data, error } = await supabase
      .from('ama_questions')
      .select('*')
      .eq('ama_id', amaId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async submitQuestion(question: Partial<AMAQuestion>): Promise<AMAQuestion> {
    const questionData = { ...question };
    delete (questionData as any).status; // Remove status if present
    
    const { data, error } = await supabase
      .from('ama_questions')
      .insert([questionData as any])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateQuestionStatus(
    questionId: string,
    status: AMAQuestionStatus
  ): Promise<AMAQuestion> {
    const { data, error } = await supabase
      .from('ama_questions')
      .update({ status })
      .eq('id', questionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteQuestion(questionId: string): Promise<void> {
    const { error } = await supabase
      .from('ama_questions')
      .delete()
      .eq('id', questionId);

    if (error) throw error;
  }

  // Answer operations
  static async getAnswersByAMA(amaId: string): Promise<AMAAnswer[]> {
    const { data, error } = await supabase
      .from('ama_answers')
      .select('*')
      .eq('ama_id', amaId)
      .order('published_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  static async getAnswerByQuestionId(questionId: string): Promise<AMAAnswer | null> {
    const { data, error } = await supabase
      .from('ama_answers')
      .select('*')
      .eq('question_id', questionId)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  static async createAnswer(answer: Partial<AMAAnswer>): Promise<AMAAnswer> {
    const { data, error } = await supabase
      .from('ama_answers')
      .insert([answer as any])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateAnswer(answerId: string, updates: Partial<AMAAnswer>): Promise<AMAAnswer> {
    const { data, error } = await supabase
      .from('ama_answers')
      .update(updates)
      .eq('id', answerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Special queries
  static async getCurrentAMA(): Promise<AMA | null> {
    // First try to find live AMA
    const { data: liveData } = await supabase
      .from('amas')
      .select('*')
      .eq('status', 'live')
      .maybeSingle();

    if (liveData) return liveData;

    // Then try to find next scheduled AMA (including today)
    const { data: plannedData } = await supabase
      .from('amas')
      .select('*')
      .eq('status', 'planned')
      .order('scheduled_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    return plannedData || null;
  }

  static async getAMAsByStatus(status: AMAStatus): Promise<AMA[]> {
    const { data, error } = await supabase
      .from('amas')
      .select('*')
      .eq('status', status)
      .order('scheduled_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
}
